/**
 * Positioned designer for pre-printed bilti books (docs/plans/print-templates.md §4, phase 3). Shown by
 * template-designer.ts instead of the block list when the template's mode is "positioned". Like the flow designer it
 * edits the template text in textarea[data-tpl-source] and fires its input event, which redraws the preview.
 *
 * The page is drawn in mm over an optional scan of the paper book (page.background, designer only, never printed).
 * Keys on the page: ←↑→↓ move the selected item 1 mm (Shift 0.1 mm) · Alt+arrows nudge everything (printer offset) ·
 * PageDown/PageUp next/previous item · Home/End first/last · Enter edit settings · Delete remove · Insert pick a field ·
 * Ctrl+↑/↓ row gap of item rows · Ctrl+Z/Ctrl+Y undo/redo · Esc back to the page. Fields can also be dragged from the
 * list onto the page, and items dragged with the mouse.
 */

interface Field { name: string; label: string; kind: string; sample: string }
interface List { name: string; label: string; fields: Field[] }
export interface PositionedCatalog { fields: Field[]; lists: List[]; required: string[]; pageSizes: string[] }

interface El {
  type: string; field?: string; text?: string; x_mm?: number; y_mm?: number; width_mm?: number; height_mm?: number; size?: number;
  bold?: boolean; align?: string; rows?: string; pitch_mm?: number; per_page?: number; items?: El[];
}
interface PPage {
  size?: string; width_mm?: number; height_mm?: number; font_size?: number; offset_x_mm?: number; offset_y_mm?: number;
  background?: string; copy_labels?: string[];
}
interface PDoc { mode?: string; page?: PPage; elements?: El[] }

const TYPES: Record<string, [string, string]> = {
  field: ["Field", "One value from the bilti"],
  text: ["Text", "Your own words; can include {{fields}}"],
  line: ["Line", "A horizontal line"],
  box: ["Box", "A rectangle"],
  rows: ["Item rows", "One line per goods line or charge; extra rows go to the next page"],
  copy_label: ["Copy label", "Consignor / Consignee / Office copy (Duplicate on a reprint)"],
  duplicate_mark: ["DUPLICATE mark", "Prints DUPLICATE here on every reprint (IRD)"],
};
const SIZES: Record<string, [string, number, number]> = {
  a4: ["A4 portrait", 210, 297], a4_landscape: ["A4 landscape", 297, 210], a5: ["A5 portrait", 148, 210],
  a5_landscape: ["A5 landscape", 210, 148], continuous: ["Continuous 241 × 279 mm", 241, 279], custom: ["Custom size", 0, 0],
};
const ALIGNS: [string, string][] = [["", "Left"], ["center", "Centre"], ["right", "Right"]];
const MAX_BACKGROUND = 400_000; // TemplateValidator.MaxBackgroundLength
const PT_MM = 25.4 / 72;
const FOCUSABLE = "input, select, textarea, button";
const round = (v: number) => Math.round(v * 10) / 10;
const ctrl = (e: KeyboardEvent) => e.ctrlKey || e.metaKey;

export function positionedDesigner(host: HTMLElement, source: HTMLTextAreaElement, catalog: PositionedCatalog): void {
  const fieldOf = (name?: string) => catalog.fields.find((f) => f.name === name);
  const listOf = (name?: string) => catalog.lists.find((l) => l.name === name);

  // ---- state and history (same rules as the flow designer) --------------------------------------------------------
  let doc: PDoc = {};
  let error: string | null = null;
  const history: string[] = [source.value];
  let at = 0;
  let lastKey: string | null = null;
  let selected = -1; // -1 = the page itself
  let writing = false;
  let focusNext: string | null = null;

  const parse = (text: string): boolean => {
    try {
      const d = JSON.parse(text) as PDoc;
      if (!d || typeof d !== "object" || Array.isArray(d)) throw new Error("it isn't a template");
      doc = d;
      doc.elements ??= [];
      error = d.mode === "positioned" ? null : "this is no longer a pre-printed template; reload the page to use the block designer";
      return true;
    } catch (e) {
      error = (e as Error).message;
      return false;
    }
  };
  const els = () => (doc.elements ??= []);
  const page = () => (doc.page ??= {});

  const commit = (key: string | null, structural: boolean) => {
    const text = JSON.stringify(doc, null, 2);
    if (key !== null && key === lastKey && at === history.length - 1) history[at] = text;
    else { history.splice(at + 1); history.push(text); at = history.length - 1; }
    lastKey = key;
    writeSource(text);
    renderPaper();
    if (structural) renderPanel(true);
    renderToolbar();
  };
  const writeSource = (text: string) => {
    writing = true;
    try { source.value = text; source.dispatchEvent(new Event("input", { bubbles: true })); } finally { writing = false; }
  };
  const travel = (step: number) => {
    if (at + step < 0 || at + step >= history.length) return;
    at += step;
    lastKey = null;
    parse(history[at] ?? "{}");
    writeSource(history[at] ?? "{}");
    if (selected >= els().length) selected = els().length - 1;
    renderAll(true);
  };
  source.addEventListener("input", () => {
    if (writing) return;
    if (parse(source.value)) {
      if (lastKey === "advanced" && at === history.length - 1) history[at] = source.value;
      else { history.splice(at + 1); history.push(source.value); at = history.length - 1; }
      lastKey = "advanced";
      if (selected >= els().length) selected = -1;
    }
    renderAll(false);
  });

  // ---- page geometry ----------------------------------------------------------------------------------------------
  const paperMm = (): [number, number] => {
    const p = page();
    const s = SIZES[p.size ?? "a5_landscape"];
    return !s || p.size === "custom" ? [p.width_mm ?? 210, p.height_mm ?? 148] : [s[1], s[2]];
  };
  let scale = 3; // px per mm, fitted to the space the page has
  const px = (mm: number) => `${mm * scale}px`;

  // ---- DOM --------------------------------------------------------------------------------------------------------
  const el = <K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> & Record<string, unknown> = {}, ...kids: (globalThis.Node | string)[]) => {
    const e = document.createElement(tag);
    for (const [k, v] of Object.entries(props)) {
      if (k.startsWith("data-") || k.startsWith("aria-") || k === "role") e.setAttribute(k, String(v));
      else (e as unknown as Record<string, unknown>)[k] = v;
    }
    e.append(...kids);
    return e;
  };
  const status = el("p", { className: "tpl-dz-status", role: "status" });
  const say = (msg: string) => { status.textContent = msg; };
  const toolbar = el("div", { className: "tpl-dz-toolbar" });
  const banner = el("p", { className: "cb-alert cb-alert-warning", role: "alert", hidden: true });
  const paper = el("div", { className: "tpl-pz-paper", role: "listbox", tabIndex: 0,
    "aria-label": "Page. Arrows move the selected item (Shift: 0.1 mm), Alt+arrows nudge everything, PageDown/PageUp choose the next item, Enter edits it, Insert adds a field" });
  const stage = el("div", { className: "tpl-pz-stage" }, paper);
  const palette = el("ul", { className: "tpl-pz-palette", role: "listbox", tabIndex: 0, "aria-label": "Fields: drag one onto the page, or press Enter to add it" });
  const panel = el("div", { className: "tpl-dz-panel", role: "region", "aria-label": "Settings" });
  const need = el("p", { className: "tpl-card-meta tpl-dz-need" });
  const upload = el("input", { type: "file", accept: "image/jpeg,image/png", hidden: true });
  host.append(toolbar, banner, el("div", { className: "tpl-pz-body" }, el("div", { className: "tpl-pz-left" }, stage, palette), panel), need, status, upload);

  const button = (label: string, title: string, act: () => void, disabled = false) => {
    const b = el("button", { type: "button", className: "cb-btn cb-btn-secondary", textContent: label, title, disabled });
    b.addEventListener("click", act);
    return b;
  };

  // ---- publish check (same rule as TemplateValidator.CheckPositionedPublishable) -----------------------------------
  const missing = (): string[] => {
    const shown = new Set<string>();
    els().forEach((e) => {
      if (e.type === "rows") (e.items ?? []).forEach((c) => shown.add(`${e.rows}.${c.field}`));
      if (e.field) shown.add(e.field);
      [...(e.text ?? "").matchAll(/\{\{\s*([a-z0-9_.]+)\s*\}\}/g)].forEach((m) => shown.add(m[1]!));
    });
    const out = catalog.required.filter((r) => !shown.has(r)).map((r) => fieldOf(r)?.label ?? r);
    if (!els().some((e) => e.type === "copy_label")) out.push("Copy label");
    if (!els().some((e) => e.type === "duplicate_mark")) out.push("DUPLICATE mark");
    return out;
  };

  // ---- operations -------------------------------------------------------------------------------------------------
  const add = (e: El, focusPage = true) => {
    els().push(e);
    selected = els().length - 1;
    commit(null, true);
    describe();
    if (focusPage) paper.focus();
  };
  const addField = (name: string, x?: number, y?: number) => {
    const last = els()[selected];
    add({ type: "field", field: name, x_mm: round(x ?? Math.min((last?.x_mm ?? 5) + 5, paperMm()[0] - 20)), y_mm: round(y ?? Math.min((last?.y_mm ?? 5) + 6, paperMm()[1] - 6)), width_mm: 40 });
  };
  const remove = () => {
    if (selected < 0) return;
    els().splice(selected, 1);
    selected = Math.min(selected, els().length - 1);
    commit(null, true);
  };
  const moveBy = (dx: number, dy: number) => {
    const e = els()[selected];
    if (!e) return;
    e.x_mm = round(Math.max(0, (e.x_mm ?? 0) + dx));
    e.y_mm = round(Math.max(0, (e.y_mm ?? 0) + dy));
    commit(`move:${selected}`, false);
    syncPanel(e);
    describe();
  };
  const nudgeAll = (dx: number, dy: number) => {
    const p = page();
    const clamp = (v: number) => round(Math.max(-20, Math.min(20, v)));
    p.offset_x_mm = clamp((p.offset_x_mm ?? 0) + dx);
    p.offset_y_mm = clamp((p.offset_y_mm ?? 0) + dy);
    if (p.offset_x_mm === 0) delete p.offset_x_mm;
    if (p.offset_y_mm === 0) delete p.offset_y_mm;
    commit("nudge", selected < 0);
    describe();
  };
  const pitchBy = (d: number) => {
    const e = els()[selected];
    if (e?.type !== "rows") return;
    e.pitch_mm = round(Math.max(2, Math.min(50, (e.pitch_mm ?? 6) + d)));
    commit(`pitch:${selected}`, false);
    syncPanel(e);
    describe();
  };

  /** Tells screen readers (and everyone, in the status line) where the selected item is now. */
  const describe = () => {
    const p = page();
    const offset = `printer offset ${(p.offset_x_mm ?? 0).toFixed(1)} / ${(p.offset_y_mm ?? 0).toFixed(1)} mm`;
    const e = els()[selected];
    if (!e) { say(`Page selected · ${offset}`); return; }
    const pitch = e.type === "rows" ? `, rows ${e.pitch_mm ?? 6} mm apart` : "";
    say(`${name(e)} at ${(e.x_mm ?? 0).toFixed(1)}, ${(e.y_mm ?? 0).toFixed(1)} mm, ${e.width_mm ?? 40} mm wide${pitch} · ${offset}`);
  };
  const name = (e: El) => e.type === "field" ? fieldOf(e.field)?.label ?? e.field ?? "Field"
    : e.type === "rows" ? `${TYPES.rows![0]}: ${listOf(e.rows)?.label ?? e.rows ?? ""}` : TYPES[e.type]?.[0] ?? e.type;
  /** What the item shows on screen: the sample value, so the layout reads like a printed bilti. */
  const preview = (e: El): string => {
    switch (e.type) {
      case "field": return fieldOf(e.field)?.sample || fieldOf(e.field)?.label || "?";
      case "text": return (e.text ?? "").replace(/\{\{\s*([a-z0-9_.]+)\s*\}\}/g, (_, f: string) => fieldOf(f)?.sample ?? f);
      case "copy_label": return page().copy_labels?.[0] ?? "Original";
      case "duplicate_mark": return "DUPLICATE";
      default: return "";
    }
  };

  // ---- the page ---------------------------------------------------------------------------------------------------
  const renderPaper = () => {
    banner.hidden = !error;
    banner.textContent = error ? `The designer can't show this template: ${error}. Fix it in the Advanced tab.` : "";
    stage.hidden = !!error;
    if (error) return;
    const [w, h] = paperMm();
    scale = Math.max(1.5, Math.min(4, (stage.clientWidth || 640) / w));
    paper.style.width = px(w);
    paper.style.height = px(h);
    // The scan as an <img> (no CSS string-building), and only an inline image: never a URL to somewhere else.
    const bg = page().background;
    const scan = bg?.startsWith("data:image/") ? [el("img", { className: "tpl-pz-scan", src: bg, alt: "", draggable: false })] : [];
    const base = page().font_size ?? 9;
    paper.replaceChildren(...scan, ...els().map((e, i) => {
      const id = `tpl-pz-el-${i}`;
      const box = el("div", { id, role: "option", className: `tpl-pz-el tpl-pz-${e.type}`, "aria-selected": i === selected, "aria-label": name(e), title: name(e), "data-index": i });
      box.style.left = px(e.x_mm ?? 0);
      box.style.top = px(e.y_mm ?? 0);
      box.style.width = px(e.width_mm ?? 40);
      box.style.fontSize = `${(e.size ?? base) * PT_MM * scale}px`;
      if (e.bold) box.style.fontWeight = "700";
      if (e.align) box.style.textAlign = e.align;
      if (e.type === "box") box.style.height = px(e.height_mm ?? 10);
      if (e.type === "rows") {
        const pitch = e.pitch_mm ?? 6;
        const per = e.per_page ?? 1;
        box.style.height = px(pitch * per);
        box.style.width = px(Math.max(e.width_mm ?? 0, ...(e.items ?? []).map((c) => (c.x_mm ?? 0) + (c.width_mm ?? 40))));
        for (let r = 0; r < per; r++) {
          const line = el("div", { className: "tpl-pz-rowline" });
          line.style.top = px(r * pitch);
          line.style.height = px(pitch);
          box.append(line);
        }
        const list = listOf(e.rows);
        (e.items ?? []).forEach((c) => {
          const col = el("div", { className: "tpl-pz-col", textContent: list?.fields.find((f) => f.name === c.field)?.sample ?? c.field ?? "" });
          col.style.left = px(c.x_mm ?? 0);
          col.style.width = px(c.width_mm ?? 40);
          if (c.align) col.style.textAlign = c.align;
          box.append(col);
        });
        const handle = el("div", { className: "tpl-pz-pitch", title: "Drag to change the gap between rows (Ctrl+↑/↓)" });
        handle.style.top = px(pitch);
        handle.addEventListener("pointerdown", (ev) => drag(ev, i, "pitch"));
        box.append(handle);
      } else box.append(preview(e));
      box.addEventListener("pointerdown", (ev) => drag(ev, i, "move"));
      if (i === selected) paper.setAttribute("aria-activedescendant", id);
      return box;
    }));
    if (selected < 0) paper.removeAttribute("aria-activedescendant");
  };

  /** Mouse: drag an item to move it, or the row handle to change the row gap. One undo step per drag. */
  const drag = (ev: PointerEvent, i: number, what: "move" | "pitch") => {
    if (ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    if (selected !== i) { selected = i; renderPanel(false); renderPaper(); }
    paper.focus();
    const e = els()[i]!;
    const start = { x: ev.clientX, y: ev.clientY, ex: e.x_mm ?? 0, ey: e.y_mm ?? 0, p: e.pitch_mm ?? 6 };
    lastKey = null;
    const onMove = (m: PointerEvent) => {
      const dx = (m.clientX - start.x) / scale;
      const dy = (m.clientY - start.y) / scale;
      if (what === "move") { e.x_mm = round(Math.max(0, start.ex + dx)); e.y_mm = round(Math.max(0, start.ey + dy)); }
      else e.pitch_mm = round(Math.max(2, Math.min(50, start.p + dy)));
      commit(`drag:${i}`, false);
      syncPanel(e);
      describe();
    };
    const onUp = () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); lastKey = null; };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  paper.addEventListener("pointerdown", (ev) => { if (ev.target === paper) select(-1); });
  paper.addEventListener("dragover", (ev) => { ev.preventDefault(); if (ev.dataTransfer) ev.dataTransfer.dropEffect = "copy"; });
  paper.addEventListener("drop", (ev) => {
    ev.preventDefault();
    const f = ev.dataTransfer?.getData("text/x-tpl-field");
    if (!f) return;
    const r = paper.getBoundingClientRect();
    addField(f, Math.max(0, (ev.clientX - r.left) / scale), Math.max(0, (ev.clientY - r.top) / scale));
  });

  // ---- the field list (drag source; Enter adds) -------------------------------------------------------------------
  let paletteAt = 0;
  const renderPalette = () => {
    palette.replaceChildren(...catalog.fields.map((f, i) => {
      const li = el("li", { id: `tpl-pz-f-${i}`, role: "option", draggable: true, textContent: f.label, title: `${f.name} · e.g. ${f.sample}`, "aria-selected": i === paletteAt });
      li.addEventListener("dragstart", (ev) => { ev.dataTransfer?.setData("text/x-tpl-field", f.name); });
      li.addEventListener("dblclick", () => addField(f.name));
      return li;
    }));
    palette.setAttribute("aria-activedescendant", `tpl-pz-f-${paletteAt}`);
    palette.querySelector("[aria-selected=true]")?.scrollIntoView({ block: "nearest" });
  };
  palette.addEventListener("keydown", (e) => {
    const go = (j: number) => { e.preventDefault(); paletteAt = Math.max(0, Math.min(catalog.fields.length - 1, j)); renderPalette(); };
    if (e.key === "ArrowDown") go(paletteAt + 1);
    else if (e.key === "ArrowUp") go(paletteAt - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(catalog.fields.length - 1);
    else if (e.key === "PageDown") go(paletteAt + 10);
    else if (e.key === "PageUp") go(paletteAt - 10);
    else if (e.key === "Enter") { e.preventDefault(); const f = catalog.fields[paletteAt]; if (f) addField(f.name); }
    else if (e.key.length === 1 && /\S/.test(e.key) && !ctrl(e) && !e.altKey) {
      // type a letter to jump to the next field starting with it
      const k = e.key.toLowerCase();
      const n = catalog.fields.length;
      for (let s = 1; s <= n; s++) if (catalog.fields[(paletteAt + s) % n]!.label.toLowerCase().startsWith(k)) { go((paletteAt + s) % n); break; }
    }
  });

  // ---- toolbar ----------------------------------------------------------------------------------------------------
  const renderToolbar = () => {
    toolbar.replaceChildren(
      button("Add field", "Insert: choose a field from the list (or drag one onto the page)", () => palette.focus(), !!error),
      button("Add text", "Your own words", () => add({ type: "text", text: "Text", x_mm: 10, y_mm: 10, width_mm: 40 }), !!error),
      button("Add line", "A horizontal line", () => add({ type: "line", x_mm: 10, y_mm: 10, width_mm: 40 }), !!error),
      button("Add box", "A rectangle", () => add({ type: "box", x_mm: 10, y_mm: 10, width_mm: 40, height_mm: 10 }), !!error),
      button("Add item rows", "Goods lines or charges, one per row", () => add({ type: "rows", rows: catalog.lists[0]?.name ?? "", x_mm: 10, y_mm: 60, width_mm: 100, pitch_mm: 6, per_page: 5,
        items: (catalog.lists[0]?.fields ?? []).slice(0, 2).map((f, i) => ({ type: "field", field: f.name, x_mm: i * 50, width_mm: 45 })) }), !!error),
      ...(els().some((e) => e.type === "copy_label") ? [] : [button("Add copy label", "Required", () => add({ type: "copy_label", x_mm: 10, y_mm: 5, width_mm: 50 }), !!error)]),
      ...(els().some((e) => e.type === "duplicate_mark") ? [] : [button("Add DUPLICATE mark", "Required: reprints say DUPLICATE here", () => add({ type: "duplicate_mark", x_mm: 10, y_mm: 10, width_mm: 40, size: 12 }), !!error)]),
      button("Remove", "Delete", remove, !!error || selected < 0),
      button("Undo", "Ctrl+Z", () => travel(-1), at === 0),
      button("Redo", "Ctrl+Y", () => travel(1), at >= history.length - 1),
      button("Print calibration sheet", "A mm grid with corner marks and the field boxes, to line up your printer; then set the printer offset", calibrate, !!error));
    const left = error ? [] : missing();
    need.textContent = left.length ? `Still needed before publishing: ${left.join(", ")}.` : "Ready to publish: every required field, the copy label and the DUPLICATE mark are on the page.";
  };
  const calibrate = () => {
    const form = document.querySelector<HTMLFormElement>("form[data-tpl-calibration]");
    const json = form?.querySelector<HTMLInputElement>("input[name=Json]");
    if (!form || !json) return;
    json.value = source.value;
    form.requestSubmit();
  };

  // ---- the scan of the paper book (designer only) ------------------------------------------------------------------
  /** Shrinks the scan until it fits the template size limit, as JPEG; the scan only guides placing the fields. */
  const shrink = (img: HTMLImageElement): string | null => {
    for (const [side, quality] of [[1600, 0.7], [1200, 0.6], [900, 0.5], [700, 0.45]] as const) {
      const k = Math.min(1, side / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement("canvas");
      c.width = Math.round(img.naturalWidth * k);
      c.height = Math.round(img.naturalHeight * k);
      c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
      const url = c.toDataURL("image/jpeg", quality);
      if (url.length <= MAX_BACKGROUND) return url;
    }
    return null;
  };
  upload.addEventListener("change", () => {
    const file = upload.files?.[0];
    upload.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png)$/.test(file.type)) { say("Choose a JPEG or PNG scan of the paper book."); return; }
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      const url = shrink(img);
      if (!url) { say("That scan is too large even when made smaller; crop it to the bilti page and try again."); return; }
      page().background = url;
      commit(null, true);
      say("Scan added under the page. It is only a guide here and is never printed.");
    };
    img.onerror = () => say("That file isn't an image the browser can read.");
    img.src = URL.createObjectURL(file);
  });

  // ---- settings ---------------------------------------------------------------------------------------------------
  const put = <T extends object>(obj: T, prop: keyof T & string, value: unknown) => {
    const rec = obj as Record<string, unknown>;
    if (value === "" || value === false || value === undefined || (typeof value === "number" && Number.isNaN(value))) delete rec[prop];
    else rec[prop] = value;
  };
  const row = (label: string, control: HTMLElement) => el("label", { className: "tpl-dz-field" }, el("span", { textContent: label }), control);
  const text = (key: string, value: string | undefined, onInput: (v: string) => void, placeholder = "") => {
    const i = el("input", { value: value ?? "", placeholder, "data-key": key });
    i.addEventListener("input", () => { onInput(i.value); commit(key, false); });
    return i;
  };
  const num = (key: string, value: number | undefined, onInput: (v: number) => void, min: number, max: number, step = 0.1) => {
    const i = el("input", { type: "number", value: value === undefined ? "" : String(value), min: String(min), max: String(max), step: String(step), "data-key": key });
    i.addEventListener("input", () => { onInput(i.value === "" ? NaN : Number(i.value)); commit(key, false); describe(); });
    return i;
  };
  const choose = (key: string, value: string | undefined, options: [string, string][], onChange: (v: string) => void, structural = false) => {
    const s = el("select", { "data-key": key }, ...options.map(([v, l]) => el("option", { value: v, textContent: l, selected: v === (value ?? "") })));
    s.addEventListener("change", () => { onChange(s.value); commit(key, structural); });
    return s;
  };
  const tick = (key: string, value: boolean | undefined, onChange: (v: boolean) => void) => {
    const c = el("input", { type: "checkbox", checked: !!value, "data-key": key });
    c.addEventListener("change", () => { onChange(c.checked); commit(key, false); });
    return c;
  };
  /** Keeps the x/y/gap boxes in step when the item is moved with the keys or the mouse. */
  const syncPanel = (e: El) => {
    const set = (k: string, v: number | undefined) => {
      const i = panel.querySelector<HTMLInputElement>(`[data-key="${k}"]`);
      if (i && document.activeElement !== i) i.value = v === undefined ? "" : String(v);
    };
    set(`${selected}:x`, e.x_mm);
    set(`${selected}:y`, e.y_mm);
    set(`${selected}:pitch`, e.pitch_mm);
  };

  const pageForm = (): HTMLElement[] => {
    const p = page();
    const sizes = catalog.pageSizes.filter((s) => SIZES[s]).map((s): [string, string] => [s, SIZES[s]![0]]);
    return [
      el("h4", { textContent: "Page (the paper book)" }),
      row("Paper", choose("page:size", p.size ?? "a5_landscape", sizes, (v) => put(p, "size", v), true)),
      ...(p.size === "custom" ? [row("Width (mm)", num("page:width", p.width_mm, (v) => put(p, "width_mm", v), 50, 500, 1)),
        row("Height (mm)", num("page:height", p.height_mm, (v) => put(p, "height_mm", v), 50, 1000, 1))] : []),
      row("Font size", num("page:font", p.font_size, (v) => put(p, "font_size", v), 5, 24, 0.5)),
      row("Printer offset right (mm)", num("page:ox", p.offset_x_mm, (v) => put(p, "offset_x_mm", v), -20, 20)),
      row("Printer offset down (mm)", num("page:oy", p.offset_y_mm, (v) => put(p, "offset_y_mm", v), -20, 20)),
      el("p", { className: "tpl-card-meta", textContent: "Print the calibration sheet, lay it on a page of the book, and move everything with Alt+arrows (or these boxes) until the marks line up. Negative moves left or up." }),
      row("Copy labels", text("page:copies", (p.copy_labels ?? []).join(", "), (v) => { const parts = v.split(",").map((s) => s.trim()).filter(Boolean); put(p, "copy_labels", parts.length ? parts : undefined); },
        "e.g. Consignor Copy, Consignee Copy, Office Copy")),
      el("div", { className: "tpl-dz-inline" },
        button(p.background ? "Change scan…" : "Add a scan of the book…", "A JPEG or PNG photo or scan of one page; shown under the fields, never printed", () => upload.click()),
        ...(p.background ? [button("Remove scan", "", () => { delete p.background; commit(null, true); })] : [])),
    ];
  };

  const elementForm = (i: number, e: El): HTMLElement[] => {
    const k = String(i);
    const out: HTMLElement[] = [el("h4", { textContent: name(e) }), el("p", { className: "tpl-card-meta", textContent: TYPES[e.type]?.[1] ?? "" })];
    if (e.type === "field") out.push(row("Field", choose(`${k}:field`, e.field, catalog.fields.map((f): [string, string] => [f.name, f.label]), (v) => put(e, "field", v), true)));
    if (e.type === "text") out.push(row("Text", text(`${k}:text`, e.text, (v) => put(e, "text", v), "e.g. PAN {{consignor.pan}}")));
    if (e.type === "rows") {
      const list = listOf(e.rows);
      out.push(row("Rows from", choose(`${k}:rows`, e.rows, catalog.lists.map((l): [string, string] => [l.name, l.label]), (v) => {
        e.rows = v;
        e.items = (listOf(v)?.fields ?? []).slice(0, 2).map((f, n) => ({ type: "field", field: f.name, x_mm: n * 50, width_mm: 45 }));
      }, true)));
      out.push(row("X (mm)", num(`${k}:x`, e.x_mm, (v) => put(e, "x_mm", v), 0, 1000)), row("Y (mm)", num(`${k}:y`, e.y_mm, (v) => put(e, "y_mm", v), 0, 1000)),
        row("Gap between rows (mm)", num(`${k}:pitch`, e.pitch_mm, (v) => put(e, "pitch_mm", v), 2, 50)),
        row("Rows per page", num(`${k}:per`, e.per_page, (v) => put(e, "per_page", v), 1, 100, 1)),
        row("Text size", num(`${k}:size`, e.size, (v) => put(e, "size", v), 5, 48, 0.5)));
      const cols = (e.items ??= []);
      const set = el("fieldset", { className: "tpl-dz-set" }, el("legend", { textContent: "Columns (field, from the rows' left edge, width, align)" }));
      cols.forEach((c, n) => {
        const ck = `${k}:items.${n}`;
        const line = el("div", { className: "tpl-pz-colrow", "data-item": n },
          choose(`${ck}:field`, c.field, (list?.fields ?? []).map((f): [string, string] => [f.name, f.label]), (v) => put(c, "field", v)),
          num(`${ck}:x`, c.x_mm, (v) => put(c, "x_mm", v), 0, 1000),
          num(`${ck}:w`, c.width_mm, (v) => put(c, "width_mm", v), 1, 500),
          choose(`${ck}:align`, c.align, ALIGNS, (v) => put(c, "align", v)),
          button("✕", "Remove this column", () => { cols.splice(n, 1); commit(null, true); }));
        ["Column field", "Column X (mm)", "Column width (mm)", "Column align"].forEach((l, j) => line.children[j]?.setAttribute("aria-label", l));
        set.append(line);
      });
      const unused = (list?.fields ?? []).find((f) => !cols.some((c) => c.field === f.name));
      if (unused) set.append(button("Add column", "", () => {
        cols.push({ type: "field", field: unused.name, x_mm: round(Math.max(0, ...cols.map((c) => (c.x_mm ?? 0) + (c.width_mm ?? 40) + 2))), width_mm: 30 });
        commit(null, true);
      }));
      out.push(set);
      return out;
    }
    out.push(row("X (mm)", num(`${k}:x`, e.x_mm, (v) => put(e, "x_mm", v), 0, 1000)), row("Y (mm)", num(`${k}:y`, e.y_mm, (v) => put(e, "y_mm", v), 0, 1000)),
      row("Width (mm)", num(`${k}:w`, e.width_mm, (v) => put(e, "width_mm", v), 1, 500)));
    if (e.type === "box") out.push(row("Height (mm)", num(`${k}:h`, e.height_mm, (v) => put(e, "height_mm", v), 1, 1000)));
    if (e.type !== "line" && e.type !== "box")
      out.push(row("Text size", num(`${k}:size`, e.size, (v) => put(e, "size", v), 5, 48, 0.5)),
        row("Bold", tick(`${k}:bold`, e.bold, (v) => put(e, "bold", v))), row("Align", choose(`${k}:align`, e.align, ALIGNS, (v) => put(e, "align", v))));
    return out;
  };

  const renderPanel = (keepFocus: boolean) => {
    const focusKey = focusNext ?? (keepFocus ? (document.activeElement as HTMLElement | null)?.dataset?.key : undefined);
    focusNext = null;
    if (error) { panel.replaceChildren(); return; }
    const e = els()[selected];
    panel.replaceChildren(...(e ? elementForm(selected, e) : pageForm()));
    if (focusKey) panel.querySelector<HTMLElement>(`[data-key="${CSS.escape(focusKey)}"]`)?.focus();
  };
  const renderAll = (keepFocus: boolean) => { renderPaper(); renderPanel(keepFocus); renderToolbar(); };
  const select = (i: number) => {
    if (i === selected) return;
    selected = i;
    lastKey = null;
    renderPaper();
    renderPanel(false);
    renderToolbar();
    describe();
  };

  // ---- keyboard ---------------------------------------------------------------------------------------------------
  host.addEventListener("keydown", (e) => {
    if (ctrl(e) && !e.altKey && (e.key === "z" || e.key === "Z" || e.key === "y" || e.key === "Y")) {
      e.preventDefault();
      travel(e.key === "y" || e.key === "Y" || e.shiftKey ? 1 : -1);
      return;
    }
    if (e.key === "Escape") {
      e.stopPropagation(); // not the page's "discard changes?" prompt: Esc only goes back to the page here
      paper.focus();
      return;
    }
    const t = e.target as HTMLElement;
    if (e.key === "Enter" && t.closest(".tpl-dz-panel") && (t instanceof HTMLInputElement || t instanceof HTMLSelectElement)) {
      e.preventDefault();
      const all = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((x) => !(x as HTMLButtonElement).disabled);
      const next = all[all.indexOf(t) + (e.shiftKey ? -1 : 1)];
      if (next) next.focus(); else paper.focus();
    }
  });

  paper.addEventListener("keydown", (e) => {
    if (error) return;
    const step = e.shiftKey ? 0.1 : 1;
    const arrows: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const d = arrows[e.key];
    if (d && e.altKey) { e.preventDefault(); nudgeAll(d[0], d[1]); }
    else if (d && ctrl(e)) { e.preventDefault(); if (d[1] !== 0) pitchBy(d[1] > 0 ? (e.shiftKey ? 0.1 : 0.5) : (e.shiftKey ? -0.1 : -0.5)); }
    else if (d) { e.preventDefault(); moveBy(d[0], d[1]); }
    else if (e.key === "PageDown" || e.key === "PageUp") {
      e.preventDefault();
      const n = els().length;
      if (n) select(e.key === "PageDown" ? (selected + 1) % n : (selected <= 0 ? n - 1 : selected - 1));
    }
    else if (e.key === "Home") { e.preventDefault(); select(els().length ? 0 : -1); }
    else if (e.key === "End") { e.preventDefault(); select(els().length - 1); }
    else if (e.key === "Enter" || e.key === "F2") { e.preventDefault(); panel.querySelector<HTMLElement>(FOCUSABLE)?.focus(); }
    else if (e.key === "Delete") { e.preventDefault(); remove(); }
    else if (e.key === "Insert") { e.preventDefault(); palette.focus(); }
  });

  // ---- start ------------------------------------------------------------------------------------------------------
  parse(source.value);
  renderPalette();
  renderAll(false);
  describe();
  new ResizeObserver(() => {
    const fit = Math.max(1.5, Math.min(4, (stage.clientWidth || 640) / paperMm()[0]));
    if (Math.abs(fit - scale) > 0.01) renderPaper();
  }).observe(stage);
  if (!document.activeElement || document.activeElement === document.body) paper.focus();
}
